(function () {
    'use strict';

    angular
        .module('app.core')
        .factory('performanceOptimizationSvc', performanceOptimizationSvc);

    performanceOptimizationSvc.$inject = ['$timeout', '$window'];

    function performanceOptimizationSvc($timeout, $window) {
        var service = {
            batchChartUpdates: batchChartUpdates,
            deferredSliderInit: deferredSliderInit,
            debounce: debounce,
            throttle: throttle,
            requestAnimationFramePolyfill: requestAnimationFramePolyfill
        };

        return service;

        ////////////////

        /**
         * Batch update multiple Chart.js instances to minimize forced reflows
         * @param {Object} options - Configuration for chart updates
         * @param {boolean} options.isDarkMode - Whether dark mode is enabled
         * @param {Array} options.chartInstances - Specific chart instances to update (optional)
         */
        function batchChartUpdates(options) {
            var isDarkMode = options.isDarkMode || false;
            var chartInstances = options.chartInstances || null;

            // Update global defaults first (doesn't cause reflow)
            if (typeof Chart !== 'undefined') {
                Chart.defaults.global.defaultFontColor = isDarkMode ? '#ddd' : '#484848';
                Chart.defaults.global.color = isDarkMode ? '#ddd' : '#484848';
            }

            // Use requestAnimationFrame to batch all chart updates together
            requestAnimationFramePolyfill(function () {
                var charts = chartInstances;

                // If no specific instances provided, get all charts
                if (!charts && window.Chart && window.Chart.instances) {
                    charts = Object.keys(window.Chart.instances).map(function (key) {
                        return window.Chart.instances[key];
                    }).filter(function (instance) {
                        return instance && typeof instance.update === 'function';
                    });
                }

                if (!charts || charts.length === 0) {
                    return;
                }

                // Batch all option updates first (no DOM reads)
                charts.forEach(function (chartInstance) {
                    if (chartInstance && chartInstance.options) {
                        chartInstance.options.legend = chartInstance.options.legend || {};
                        chartInstance.options.legend.labels = chartInstance.options.legend.labels || {};
                        chartInstance.options.legend.labels.fontColor = isDarkMode ? '#ddd' : '#484848';

                        if (chartInstance.options.scales) {
                            if (chartInstance.options.scales.xAxes) {
                                chartInstance.options.scales.xAxes.forEach(function (axis) {
                                    if (axis.ticks) {
                                        axis.ticks.fontColor = isDarkMode ? '#ddd' : '#484848';
                                    }
                                    if (axis.gridLines) {
                                        axis.gridLines.color = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                                    }
                                });
                            }
                            if (chartInstance.options.scales.yAxes) {
                                chartInstance.options.scales.yAxes.forEach(function (axis) {
                                    if (axis.ticks) {
                                        axis.ticks.fontColor = isDarkMode ? '#ddd' : '#484848';
                                    }
                                    if (axis.gridLines) {
                                        axis.gridLines.color = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
                                    }
                                });
                            }
                        }

                        if (chartInstance.options.tooltips) {
                            chartInstance.options.tooltips.backgroundColor = isDarkMode ? 'rgba(42, 42, 42, 0.9)' : 'rgba(0, 0, 0, 0.8)';
                            chartInstance.options.tooltips.borderColor = isDarkMode ? '#555' : '#ccc';
                        }
                    }
                });

                // Now batch all updates with animation disabled to prevent multiple reflows
                charts.forEach(function (chartInstance) {
                    if (chartInstance && typeof chartInstance.update === 'function') {
                        // Update with duration 0 to skip animation and reduce reflows
                        chartInstance.update({
                            duration: 0,
                            lazy: false
                        });
                    }
                });
            });
        }

        /**
         * Initialize tiny-slider with IntersectionObserver to defer until visible
         * @param {string} containerSelector - CSS selector for the slider container
         * @param {Object} sliderOptions - Options to pass to tns()
         * @param {Function} callback - Callback function that receives the slider instance
         */
        function deferredSliderInit(containerSelector, sliderOptions, callback) {
            var container = document.querySelector(containerSelector);

            if (!container) {
                console.warn('Slider container not found:', containerSelector);
                return;
            }

            // Check if container has valid children
            if (!container.children || container.children.length === 0) {
                console.warn('Slider container has no children:', containerSelector);
                return;
            }

            // Use IntersectionObserver to defer initialization until visible
            if ('IntersectionObserver' in window) {
                var observer = new IntersectionObserver(function (entries) {
                    entries.forEach(function (entry) {
                        if (entry.isIntersecting) {
                            // Element is visible, initialize slider
                            observer.disconnect();

                            // Use requestAnimationFrame to defer to next frame
                            requestAnimationFramePolyfill(function () {
                                try {
                                    var slider = tns(sliderOptions);
                                    if (callback && typeof callback === 'function') {
                                        callback(slider);
                                    }
                                } catch (err) {
                                    console.error('Slider initialization failed:', err);
                                }
                            });
                        }
                    });
                }, {
                    rootMargin: '50px' // Start loading 50px before it comes into view
                });

                observer.observe(container);
            } else {
                // Fallback for browsers without IntersectionObserver
                $timeout(function () {
                    try {
                        var slider = tns(sliderOptions);
                        if (callback && typeof callback === 'function') {
                            callback(slider);
                        }
                    } catch (err) {
                        console.error('Slider initialization failed:', err);
                    }
                }, 100);
            }
        }

        /**
         * Debounce function to limit execution rate
         * @param {Function} func - Function to debounce
         * @param {number} wait - Milliseconds to wait
         * @param {boolean} immediate - Whether to execute immediately
         */
        function debounce(func, wait, immediate) {
            var timeout;
            return function () {
                var context = this;
                var args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) {
                        func.apply(context, args);
                    }
                };
                var callNow = immediate && !timeout;
                $timeout.cancel(timeout);
                timeout = $timeout(later, wait);
                if (callNow) {
                    func.apply(context, args);
                }
            };
        }

        /**
         * Throttle function to limit execution rate
         * @param {Function} func - Function to throttle
         * @param {number} limit - Milliseconds to limit
         */
        function throttle(func, limit) {
            var inThrottle;
            return function () {
                var context = this;
                var args = arguments;
                if (!inThrottle) {
                    func.apply(context, args);
                    inThrottle = true;
                    $timeout(function () {
                        inThrottle = false;
                    }, limit);
                }
            };
        }

        /**
         * RequestAnimationFrame polyfill with fallback
         * @param {Function} callback - Function to execute in next frame
         */
        function requestAnimationFramePolyfill(callback) {
            var raf = $window.requestAnimationFrame ||
                $window.webkitRequestAnimationFrame ||
                $window.mozRequestAnimationFrame ||
                function (cb) {
                    return $window.setTimeout(cb, 16); // ~60fps
                };

            return raf.call($window, callback);
        }
    }
})();
